import { BrandInterface, DefaultTheme } from "@swivl-mono/types";
import { Button, ButtonColor, Modal } from "@swivl-mono/ui";
import { ReactNode, useEffect, useState } from "react";
import Resizer from "react-image-file-resizer";

import { GeneratedAvatar, dataUrlToFile, generateAvatarVariants } from "./utils/avatarGenerator";

interface AvatarGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  trigger?: ReactNode;
  brand: BrandInterface | undefined;
  onAvatarSelected: (url: string) => void;
}

const resizeFile = (file: File) => {
  return new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      300,
      300,
      "WEBP",
      80,
      0,
      (uri) => {
        resolve(uri);
      },
      "file"
    );
  });
};

export function AvatarGeneratorModal({ isOpen, onClose, brand, onAvatarSelected }: AvatarGeneratorModalProps) {
  const [variants, setVariants] = useState<GeneratedAvatar[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const effectivePrimaryBackground = brand?.theme?.primaryBackground ?? DefaultTheme.primaryBackground;
  const effectiveSecondaryBackground = brand?.theme?.secondaryBackground ?? DefaultTheme.secondaryBackground;
  const hasThemeColors = Boolean(brand?.theme?.primaryBackground || brand?.theme?.secondaryBackground);

  const generateVariants = async () => {
    if (!brand) {
      return;
    }

    setGenerationError(null);
    setIsGenerating(true);
    try {
      const generatedVariants = await generateAvatarVariants(effectivePrimaryBackground, effectiveSecondaryBackground);
      setVariants(generatedVariants);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to generate avatar variants.";
      setGenerationError(message);
      setVariants([]);
      console.error("Failed to generate avatar variants:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setVariants([]);
    setGenerationError(null);
    if (brand) {
      generateVariants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, brand]);

  const handleSelectVariant = async (variant: GeneratedAvatar) => {
    setSelectedVariantId(variant.id);
    setIsUploading(true);

    try {
      const file = dataUrlToFile(variant.dataUrl, "avatar.png");
      const resizedFile = (await resizeFile(file)) as File;

      const { uploadUrl, fileUrl } = await fetch(`${(import.meta as any).env.VITE_SERVER_URL}/api/s3/org/1/get-signed-url/file.jpg`).then((res) =>
        res.json()
      );

      await fetch(uploadUrl, {
        method: "PUT",
        body: resizedFile
      });

      onAvatarSelected(fileUrl);
      onClose();
    } catch (error) {
      console.error("Failed to upload avatar:", error);
    } finally {
      setIsUploading(false);
      setSelectedVariantId(null);
    }
  };

  const renderContent = () => {
    if (!brand) {
      return (
        <div className="p-4">
          <p className="text-center text-gray-600">Please select a Color Theme first to generate avatars.</p>
          <div className="mt-4 flex justify-center">
            <Button color={ButtonColor.ORANGE} onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      );
    }

    const themeNotice = !hasThemeColors ? (
      <p className="mb-4 text-center text-sm text-gray-600">
        The selected brand does not have background colors configured. Default colors will be used.
      </p>
    ) : null;

    if (isGenerating) {
      return (
        <div className="p-4">
          {themeNotice}
          <div className="p-8 text-center">
            <p>Generating avatar variants...</p>
          </div>
        </div>
      );
    }

    if (generationError) {
      return (
        <div className="p-4">
          <p className="mb-4 text-center text-sm text-red-600">{generationError}</p>
          <div className="mt-4 flex justify-end gap-2">
            <Button color={ButtonColor.WHITE} onClick={generateVariants}>
              Retry
            </Button>
            <Button color={ButtonColor.ORANGE} onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      );
    }

    if (variants.length === 0) {
      return (
        <div className="p-4">
          <p className="text-center text-sm text-gray-600">No variants generated. Please try again.</p>
          <div className="mt-4 flex justify-end">
            <Button color={ButtonColor.ORANGE} onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="p-4">
        {themeNotice}
        <p className="mb-4 text-sm text-gray-600">Select an avatar variant to use:</p>
        <div className="grid grid-cols-2 gap-4">
          {variants.map((variant) => (
            <div
              key={variant.id}
              className="cursor-pointer rounded border-2 border-gray-200 p-4 transition-all hover:border-blue-500"
              onClick={() => !isUploading && handleSelectVariant(variant)}
            >
              <div className="mb-2 flex items-center justify-center">
                <img src={variant.dataUrl} alt={variant.label} className="h-24 w-24 rounded-full" />
              </div>
              <p className="text-center text-sm font-medium">{variant.label}</p>
              <div className="mt-2 flex items-center justify-center">
                <div className="h-6 w-6 rounded border border-gray-300" style={{ backgroundColor: variant.backgroundColor }} />
              </div>
              {isUploading && selectedVariantId === variant.id && <p className="mt-2 text-center text-xs text-gray-500">Uploading...</p>}
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <Button color={ButtonColor.WHITE} onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={() => !isUploading && onClose()} title="Generate Avatar">
      {renderContent()}
    </Modal>
  );
}
